/**
 * File vault — encrypted, content-addressed file storage scoped to a group.
 *
 * A file is split into fixed-size chunks (~256 KB), each encrypted under a
 * per-chunk key derived from a file content key. The content key itself is
 * wrapped using the group's MLS encryption — so only group members can
 * derive it.
 *
 * The result is a `Manifest` listing the chunk CIDs (Content Identifiers)
 * and the group-encrypted content key. The manifest is the small piece of
 * metadata that travels through the OrbitDB stream as a SignedEnvelope
 * payload. The chunks themselves live in local at-rest blob storage and
 * are replicated separately (out-of-scope for alpha — TODO(beta): chunk
 * announcement + on-demand fetch over IPFS).
 *
 * Key derivation:
 *   content_key = HKDF(group_encrypt(0x00 * 32), salt=group_id, info='vault.v1')
 *   chunk_key_i = HKDF(content_key, salt=manifest_id, info=`chunk.${i}`)
 *
 * The content_key derivation goes THROUGH the group encryption so it
 * inherits MLS's forward secrecy: a member removed after the file was
 * stored cannot decrypt it from their post-removal MLS state.
 *
 * Each chunk is hashed (BLAKE3 — fast, content-addressed) and the hash
 * becomes its CID in blob storage.
 *
 * Trade-offs (worth being explicit about):
 *
 *   - Forward secrecy at the file level is coarse. A file stored at epoch
 *     N can be decrypted by anyone in the group at epoch N. If member X
 *     is removed at epoch N+1, they cannot decrypt FILES STORED AFTER
 *     their removal (the group encrypt key has rotated), but if they
 *     captured the manifest envelope before removal, they retain the
 *     ability to decrypt those particular files. This is intrinsic to
 *     "stored objects" vs "ephemeral messages" — once a member sees the
 *     content key, the cryptography can't take it back.
 *
 *   - Chunk integrity: BLAKE3 hash on each chunk. A modified chunk fails
 *     the hash check on retrieve.
 *
 *   - Chunk replication: NOT IMPLEMENTED for alpha. Manifests propagate
 *     via OrbitDB; chunks must be retrieved out-of-band (currently only
 *     work for the storing client itself). TODO(beta): chunk announce
 *     via IPFS blockstore through MeshNode.
 */

import { blake3 } from '@noble/hashes/blake3';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';

import type { Bytes } from '../identity';
import type {
  GroupEncryption,
  GroupId,
  GroupState,
} from '../envelope/GroupEncryption';
import type { StorageRouter } from '../storage/StorageRouter';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

/** Default chunk size: 256 KiB. Browser-friendly streaming with reasonable overhead. */
export const DEFAULT_CHUNK_SIZE = 256 * 1024;

const HKDF_HASH = sha256;
const CONTENT_KEY_INFO = new TextEncoder().encode('surface-mesh.vault.v1.content');
const CHUNK_KEY_INFO_PREFIX = 'surface-mesh.vault.v1.chunk.';

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC TYPES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A manifest describes how to reconstruct a file from its chunks. This is
 * what gets stored as a SignedEnvelope payload on the group's OrbitDB stream.
 */
export interface VaultManifest {
  version: 'v1';
  manifestId: string;            // random identifier; salt for chunk-key HKDF
  groupId: string;
  filename: string;
  contentType: string;
  totalSize: number;
  chunkSize: number;
  chunks: Array<{
    index: number;
    cid: string;                 // BLAKE3 of plaintext chunk, hex-encoded
    size: number;
  }>;
  /**
   * Group-encrypted seed used to derive the file's content_key. Without
   * group membership, no chunk can be decrypted.
   */
  encryptedSeed: string;         // base64 of group ciphertext
  /**
   * The new GroupState epoch after the seed encryption. Recorded so
   * peers know which MLS epoch the file is bound to.
   */
  epochAtCreation: number;
}

export interface StoreResult {
  manifest: VaultManifest;
  /** The MLS state mutated by the seed encryption — caller MUST persist. */
  groupStateAfterStore: GroupState;
}

export interface RetrieveResult {
  bytes: Uint8Array;
  groupStateAfterRetrieve: GroupState;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function toHex(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    s += bytes[i]!.toString(16).padStart(2, '0');
  }
  return s;
}

function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function fromBase64(s: string): Uint8Array {
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

async function aesGcmEncrypt(key: Uint8Array, plaintext: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw', key as BufferSource, { name: 'AES-GCM' }, false, ['encrypt'],
  );
  const nonce = randomBytes(12);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, cryptoKey, plaintext as BufferSource),
  );
  const out = new Uint8Array(12 + ct.length);
  out.set(nonce, 0);
  out.set(ct, 12);
  return out;
}

async function aesGcmDecrypt(key: Uint8Array, blob: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw', key as BufferSource, { name: 'AES-GCM' }, false, ['decrypt'],
  );
  const nonce = blob.slice(0, 12);
  const ct = blob.slice(12);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, cryptoKey, ct as BufferSource);
  return new Uint8Array(pt);
}

function deriveChunkKey(contentKey: Uint8Array, manifestId: string, chunkIndex: number): Uint8Array {
  const info = new TextEncoder().encode(`${CHUNK_KEY_INFO_PREFIX}${chunkIndex}`);
  const salt = new TextEncoder().encode(manifestId);
  return hkdf(HKDF_HASH, contentKey, salt, info, 32);
}

function deriveContentKey(seed: Uint8Array, groupId: GroupId): Uint8Array {
  const salt = new TextEncoder().encode(groupId.value);
  return hkdf(HKDF_HASH, seed, salt, CONTENT_KEY_INFO, 32);
}

// ─────────────────────────────────────────────────────────────────────────────
// VAULT
// ─────────────────────────────────────────────────────────────────────────────

export class Vault {
  #groupEnc: GroupEncryption;
  #storage: StorageRouter;
  #chunkSize: number;

  constructor(opts: {
    groupEncryption: GroupEncryption;
    storage: StorageRouter;
    chunkSize?: number;
  }) {
    this.#groupEnc = opts.groupEncryption;
    this.#storage = opts.storage;
    this.#chunkSize = opts.chunkSize ?? DEFAULT_CHUNK_SIZE;
  }

  /**
   * Store a file in the vault. Returns the manifest (which the caller
   * should sign as an envelope and place on the group's stream) and the
   * advanced group state.
   */
  async store(opts: {
    groupState: GroupState;
    filename: string;
    contentType: string;
    bytes: Uint8Array;
  }): Promise<StoreResult> {
    // 1. Generate a fresh random seed and encrypt it under the group.
    //    This is the only place we touch MLS — the resulting state
    //    advances by one application message.
    const seed = randomBytes(32);
    const seedEncResult = await this.#groupEnc.encrypt(opts.groupState, seed);
    const encryptedSeed = toBase64(seedEncResult.ciphertext);

    // 2. Derive the content key from the seed.
    const contentKey = deriveContentKey(seed, opts.groupState.groupId);

    // 3. Generate a manifest id (salts the chunk-key HKDF).
    const manifestId = toHex(randomBytes(16));

    // 4. Chunk + encrypt + store.
    const chunkCount = Math.max(1, Math.ceil(opts.bytes.length / this.#chunkSize));
    const chunks: VaultManifest['chunks'] = [];

    for (let i = 0; i < chunkCount; i++) {
      const start = i * this.#chunkSize;
      const end = Math.min(start + this.#chunkSize, opts.bytes.length);
      const plaintext = opts.bytes.slice(start, end);

      const chunkKey = deriveChunkKey(contentKey, manifestId, i);
      const ciphertext = await aesGcmEncrypt(chunkKey, plaintext);

      // CID = BLAKE3 of CIPHERTEXT. Hashing the plaintext was the original
      // design (to share blob storage across users storing identical files)
      // but that property never held: per-chunk keys are derived from the
      // random manifestId, so two stores of the same plaintext produce
      // different ciphertexts. Worse, within a single manifest, two chunks
      // with identical plaintext (common at structured-data boundaries) got
      // the same CID and overwrote each other on putBlob — retrieve then
      // failed AES-GCM auth check on all but the last duplicate. Caught by
      // the multi-chunk roundtrip test.
      const cid = toHex(blake3(ciphertext));

      await this.#storage.putBlob(opts.groupState.groupId.value, cid, ciphertext);

      chunks.push({ index: i, cid, size: plaintext.length });

      // Zero the chunk key — forward secrecy at the per-chunk level.
      chunkKey.fill(0);
    }

    // Zero the content key + seed.
    contentKey.fill(0);
    seed.fill(0);

    const manifest: VaultManifest = {
      version: 'v1',
      manifestId,
      groupId: opts.groupState.groupId.value,
      filename: opts.filename,
      contentType: opts.contentType,
      totalSize: opts.bytes.length,
      chunkSize: this.#chunkSize,
      chunks,
      encryptedSeed,
      epochAtCreation: opts.groupState.epoch,
    };

    return {
      manifest,
      groupStateAfterStore: seedEncResult.newState,
    };
  }

  /**
   * Retrieve a file from the vault using a manifest. The group state must
   * be at-or-after the epoch the file was stored at (the seed decryption
   * goes through MLS, which enforces this).
   *
   * Returns the reassembled file and the advanced group state.
   */
  async retrieve(opts: {
    groupState: GroupState;
    manifest: VaultManifest;
  }): Promise<RetrieveResult> {
    if (opts.manifest.version !== 'v1') {
      throw new Error(`Unsupported vault manifest version: ${opts.manifest.version}`);
    }
    if (opts.manifest.groupId !== opts.groupState.groupId.value) {
      throw new Error('Manifest groupId does not match groupState');
    }

    // 1. Decrypt the seed via the group.
    const seedCT = fromBase64(opts.manifest.encryptedSeed);
    const seedDecResult = await this.#groupEnc.decrypt(opts.groupState, seedCT);
    const seed = seedDecResult.plaintext;

    // 2. Re-derive content key.
    const contentKey = deriveContentKey(seed, opts.groupState.groupId);

    // 3. Fetch + decrypt each chunk, verify CID.
    // Pre-allocate output buffer at the known total size.
    const out = new Uint8Array(opts.manifest.totalSize);
    for (const chunk of opts.manifest.chunks) {
      const ciphertext = await this.#storage.getBlob(opts.manifest.groupId, chunk.cid);
      if (!ciphertext) {
        throw new Error(`Vault: chunk ${chunk.cid} (index ${chunk.index}) not available locally`);
      }

      // Verify CID matches the stored ciphertext (CID = blake3(ciphertext)).
      const actualCid = toHex(blake3(ciphertext));
      if (actualCid !== chunk.cid) {
        throw new Error(
          `Vault: chunk ${chunk.index} CID mismatch (expected ${chunk.cid}, got ${actualCid})`,
        );
      }

      const chunkKey = deriveChunkKey(contentKey, opts.manifest.manifestId, chunk.index);
      const plaintext = await aesGcmDecrypt(chunkKey, ciphertext);
      chunkKey.fill(0);

      out.set(plaintext, chunk.index * opts.manifest.chunkSize);
    }

    // Zero the content key and seed.
    contentKey.fill(0);
    (seed as Uint8Array).fill(0);

    return {
      bytes: out,
      groupStateAfterRetrieve: seedDecResult.newState,
    };
  }

  /**
   * Delete a manifest's chunks from local storage. Note: in a replicated
   * deployment, other peers may still have copies; this only frees local
   * disk. To truly destroy a file across a group, all members must
   * delete + the group must rotate epochs (which prevents new
   * decryptions but doesn't recover already-decrypted plaintexts).
   */
  async delete(manifest: VaultManifest): Promise<void> {
    for (const chunk of manifest.chunks) {
      await this.#storage.deleteBlob(manifest.groupId, chunk.cid);
    }
  }
}

// Re-export Bytes type so callers don't have to import it from elsewhere.
export type { Bytes };
